import React from 'react';
import { css } from '@emotion/react';

import UserDropdown from '$/components/ui/Input/UserDropdown';
import {
  ProductType,
  UserRole,
  useUpdateBillingContactsMutation,
  useUpdateRewardsContactMutation,
} from '$/GraphQL/schema/schema';
import { BillingContactsInput } from '$/components/ui/Input/BillingContacts/BillingContacts';

import { usePlanPageContext } from './PlanPage';
import PermissionedLink from '$/components/Router/PermissionedLink';
import { getRoute, rolesPermissionsMatrix } from '$/shared/frontend/rolesPermissionsMatrix';
import Form from '$/components/ui/Form';
import { Popup } from '$/components/ui/Popup';
import Settings from '$/components/ui/Settings';

export function useShowTeamRewards(): boolean {
  const { team } = usePlanPageContext();
  return !team.purchasedProducts.find((p) => p.type === ProductType.RemoveBranding);
}

export const RewardsContact: React.FC = () => {
  const { team } = usePlanPageContext();
  const [updateRewardsContact] = useUpdateRewardsContactMutation();

  const profileTeamPath = getRoute(rolesPermissionsMatrix.accountTeam.path, {});
  const rewardsPath = getRoute(rolesPermissionsMatrix.profileMyRewards.path, {});

  if (team.purchasedProducts.find((p) => p.type === ProductType.RemoveBranding)) {
    return null;
  }

  return (
    <Settings.Section id='rewards'>
      <Form
        mode={'onChange'}
        submitMode={Form.SubmitMode.onChangeDebounced}
        defaultValues={team}
        onSubmit={async (data): Promise<void> => {
          await updateRewardsContact({
            variables: {
              userId: data.brandingPromoterId,
            },
          });
        }}
      >
        <Settings.Subsection>
          <Settings.SectionTitle>Team rewards</Settings.SectionTitle>
          <Settings.Description>
            <span>
              Get rewarded if someone clicks “Powered by eWebinar” on one of your landing pages,
              widgets, or webinars, gets taken to our website, and later becomes our customer. To
              set this up, you or a team member must first enroll in{' '}
              <PermissionedLink to={rewardsPath}>our rewards program.</PermissionedLink>. Then,
              assign that person as the Team rewards recipient below.
            </span>
          </Settings.Description>
          <Settings.Setting>
            Team rewards recipient{' '}
            <Popup
              className='inline-flex cursor-pointer mr-3/4e'
              content={
                <span>
                  "Powered by eWebinar" can be seen by your webinar registrants and attendees in
                  several places and links to our{' '}
                  <a href='https://ewebinar.com' target='_blank'>
                    home page
                  </a>
                  . It shows up on your registration pages and forms, in the webinar room, and at
                  the bottom of your notification emails. As a bootstrapped startup, this kind of
                  exposure is vital to our growth.
                  <p />
                  In exchange, we are happy to share the love. You or someone on{' '}
                  <PermissionedLink to={profileTeamPath}>your eWebinar team</PermissionedLink> may
                  receive $100 for every referral who becomes an eWebinar customer that came to us
                  via one of your links. To set this up, ask the chosen team member to sign up for{' '}
                  <PermissionedLink to={rewardsPath}>our rewards program</PermissionedLink> (or
                  create a new team member expressly for this purpose) and then select their name
                  from the <b>Team rewards recipient</b> drop-down. It's that simple!
                  <br />
                  <br />
                  Alternatively, you may have all eWebinar branding removed from your webinars if
                  you subscribe to the <a href='#addons'>Remove eWebinar branding add-on.</a>
                </span>
              }
            />
          </Settings.Setting>
          <Form.Field
            name='brandingPromoterId'
            css={css`
              max-width: 25rem;
              .user-dropdown-email {
                max-width: 12rem;
                overflow: hidden;
                text-overflow: ellipsis;
                @media screen and (min-width: 920px) {
                  max-width: 15rem;
                }
              }
            `}
            component={
              <UserDropdown
                userFilter={(user): boolean => Boolean(user.integrations?.firstPromoterToken)}
                allowNone
                enableEdit={false}
                isActive
              />
            }
          />
        </Settings.Subsection>
      </Form>
    </Settings.Section>
  );
};

export const BillingContacts: React.FC = () => {
  const { team } = usePlanPageContext();
  const [updateBillingContacts] = useUpdateBillingContactsMutation();

  return (
    <Form
      mode={'onChange'}
      submitMode={Form.SubmitMode.onChangeDebounced}
      defaultValues={team}
      onSubmit={async (data): Promise<void> => {
        await updateBillingContacts({
          variables: {
            data,
          },
        });
      }}
    >
      <Settings.Subsection>
        <Settings.SectionTitle>Billing contacts</Settings.SectionTitle>
        <Settings.Description>Invoices are emailed to all billing contacts.</Settings.Description>
        <Settings.Setting>Primary billing contact</Settings.Setting>
        <Form.Field
          name='primaryBillingContact'
          css={css`
            max-width: 25rem;
            .user-dropdown-email {
              max-width: 12rem;
              overflow: hidden;
              text-overflow: ellipsis;
              @media screen and (min-width: 920px) {
                max-width: 15rem;
              }
            }
          `}
          component={
            <UserDropdown
              userFilter={(user): boolean => [UserRole.Admin].includes(user.role as UserRole)}
              enableEdit={false}
              isActive
            />
          }
        />

        <Settings.Setting>Additional billing contacts</Settings.Setting>
        <Form.Field name='billingContacts' isArrayValue component={<BillingContactsInput />} />

        <Form.ErrorMessage />
      </Settings.Subsection>

      <Form.StopNonHubspotForm />
    </Form>
  );
};
